/**
 * Dialog: a centred modal.
 *
 *   <Dialog isOpen={open} onClose={() => setOpen(false)} title="Delete API key" width="sm"
 *     description="This can't be undone."
 *     footer={<>
 *       <Button variant="secondary" onClick={close}>Cancel</Button>
 *       <Button variant="danger" loading={deleting} onClick={confirm}>Delete key</Button>
 *     </>}>
 *     <p>Apps using "Production" will stop working.</p>
 *   </Dialog>
 *
 * Accessibility built in: role="dialog", aria-modal, labelled by the title (and described by `description`),
 * Escape closes (only the top-most overlay), focus moves inside on open (first [data-autofocus], else the first
 * focusable element, or `initialFocus`), Tab is trapped, and focus returns to the opener on close. Scroll lock is
 * ref-counted, so stacked dialogs behave and a closed dialog never touches the page.
 *
 * width     sm | md (default) | lg | xl | 2xl ... 5xl | full, or any max-w-* class (older call sites)
 * dismissible={false} blocks Escape and backdrop clicks (use while a request is in flight); the close button hides.
 * footer    right-aligned action row pinned under the scrolling body.
 * bodyClassName overrides the body padding (default p-6).
 */
import { ReactNode, RefObject, useId, useRef } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import IconButton from "./IconButton";
import { useEscape, useFocusTrap, useLayer, useScrollLock } from "@/hooks/useOverlay";

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  width?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "full" | string;
  description?: ReactNode;
  footer?: ReactNode;
  dismissible?: boolean;
  initialFocus?: RefObject<HTMLElement | null>;
  bodyClassName?: string;
}

const WIDTHS: Record<string, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  full: "max-w-full",
};

export default function Dialog({
  isOpen,
  onClose,
  title,
  children,
  width = "md",
  description,
  footer,
  dismissible = true,
  initialFocus,
  bodyClassName = "p-6",
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  const isTop = useLayer(isOpen);
  const close = () => dismissible && onClose();
  useEscape(isOpen, close, isTop);
  useScrollLock(isOpen);
  useFocusTrap(panelRef, isOpen, { initialFocus, isTop });

  const content = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16, ease: "easeOut" }}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={close} aria-hidden="true" />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description ? descId : undefined}
            tabIndex={-1}
            className={`relative z-10 flex max-h-[90dvh] w-full flex-col overflow-hidden rounded-card border border-line bg-raised shadow-modal outline-none ${WIDTHS[width] ?? width}`}
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-6 py-4">
              <div className="min-w-0">
                <h2 id={titleId} className="text-base font-semibold text-fg">
                  {title}
                </h2>
                {description && (
                  <p id={descId} className="mt-1 text-13 text-fg-muted">
                    {description}
                  </p>
                )}
              </div>
              {dismissible && <IconButton icon="mdi:close" label="Close" size="sm" onClick={onClose} className="-mr-2 -mt-1" />}
            </div>
            <div className={`min-h-0 flex-1 overflow-y-auto ${bodyClassName}`}>{children}</div>
            {footer && (
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line px-6 py-4">
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof window === "undefined") return null;
  return ReactDOM.createPortal(content, document.body);
}
